import type { AccountingLocation, ExpenseCategory, ExpenseRow } from "@dentacore/shared-types";
import { sumByCurrency } from "@dentacore/shared-types";

import { formatAmounts } from "../patients/money";

import { MonthSelect, YearPicker } from "./controls";
import { money, monthName, monthStarted, niceDay, plural } from "./format";
import styles from "./accounting.module.css";
import { PagedRows } from "../ui/paging";

export interface ExpensePeriod {
  year: number;
  /** 0 is the whole year. */
  month: number;
}

/**
 * Expenses — rent, utilities, lab invoices, anything the clinic pays that is
 * not a salary or a supplier order (AC-D4).
 *
 * Each belongs to one location or is *shared* (AC-D9). When only some
 * locations are selected, a shared expense counts only its share here, so a
 * *Counted here* column says how much of each one this selection carries — the
 * figure the total under it adds up.
 */
export function ExpensesTab({
  period,
  onPeriod,
  yearOptions,
  today,
  expenses,
  categories,
  locations,
  allSelected,
  selectionLabel,
  currency,
  canManage,
  onEdit,
  onCategories,
}: {
  period: ExpensePeriod;
  onPeriod: (period: ExpensePeriod) => void;
  yearOptions: readonly number[];
  today: string;
  /** Null while loading. Newest first, as the API sends them. */
  expenses: readonly ExpenseRow[] | null;
  categories: readonly ExpenseCategory[] | null;
  locations: readonly AccountingLocation[];
  allSelected: boolean;
  selectionLabel: string;
  currency: string;
  canManage: boolean;
  onEdit: (expense: ExpenseRow) => void;
  onCategories: () => void;
}): React.JSX.Element {
  // Per currency (QA4-D6): a shared expense is in the home currency, a
  // location's in its own, and the two are never added.
  const total = sumByCurrency(
    (expenses ?? []).map((row) => ({ currency: row.currency, cents: row.countedCents })),
  );
  const categoryName = (id: string): string =>
    (categories ?? []).find((category) => category.id === id)?.name ?? "—";
  const locationName = (id: string): string =>
    locations.find((location) => location.id === id)?.name ?? "Another location";

  return (
    <>
      <div className={styles.controls}>
        <YearPicker
          years={yearOptions}
          value={period.year}
          onChange={(year) => {
            // A month that has not begun in the new year falls back to the whole year.
            const keep = period.month !== 0 && monthStarted(year, period.month, today);
            onPeriod({ year, month: keep ? period.month : 0 });
          }}
          testId="accounting-expenses-year"
        />
        <MonthSelect
          year={period.year}
          today={today}
          value={period.month}
          onChange={(month) => onPeriod({ year: period.year, month })}
          label="Month"
          testId="accounting-expenses-month"
        />
        <span className={styles.spacer} />
        {canManage && (
          <button
            type="button"
            className={styles.ghost}
            onClick={onCategories}
            data-testid="accounting-categories-open"
          >
            Categories
          </button>
        )}
      </div>

      <section className={styles.card} data-testid="accounting-expenses">
        <div className={styles.cardHead}>
          <h2 className={styles.cardTitle}>
            {period.month === 0
              ? String(period.year)
              : `${monthName(period.month)} ${String(period.year)}`}
          </h2>
          {expenses !== null && (
            <p className={styles.note}>
              {plural(expenses.length, "expense")}, {selectionLabel}
            </p>
          )}
          <span className={styles.spacer} />
          <b className={styles.num} data-testid="accounting-expenses-total">
            {formatAmounts(total, currency)}
          </b>
        </div>

        {expenses === null ? (
          <p className={styles.blank}>Loading…</p>
        ) : expenses.length === 0 ? (
          <p className={styles.blank}>
            {canManage
              ? "No expenses here yet. Use Add expense to record rent, a lab invoice or any other cost."
              : "No expenses here yet."}
          </p>
        ) : (
          <PagedRows rows={expenses} per={12} noun="expenses">
            {(shown) => (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Category</th>
                      <th>Location</th>
                      <th>Note</th>
                      <th className={styles.right}>Amount</th>
                      {!allSelected && <th className={styles.right}>Counted here</th>}
                      {canManage && (
                        <th>
                          <span className={styles.srOnly}>Edit</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((row) => (
                      <tr
                        key={row.id}
                        data-ring="acc.expense"
                        data-ring-id={row.id}
                        data-testid="expense-row"
                      >
                        <td className={styles.num}>{niceDay(row.paidOn)}</td>
                        <td>{categoryName(row.categoryId)}</td>
                        <td>
                          {row.branchId === null ? (
                            <span className={`${styles.chip} ${styles.chipBlue}`}>Shared</span>
                          ) : (
                            locationName(row.branchId)
                          )}
                        </td>
                        <td>{row.note ?? ""}</td>
                        <td className={styles.right}>{money(row.amountCents, row.currency)}</td>
                        {!allSelected && (
                          <td className={styles.right}>{money(row.countedCents, row.currency)}</td>
                        )}
                        {canManage && (
                          <td className={styles.right}>
                            <button
                              type="button"
                              className={styles.quiet}
                              onClick={() => onEdit(row)}
                              aria-label={`Edit the expense of ${niceDay(row.paidOn)}`}
                              data-testid="expense-edit"
                            >
                              Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PagedRows>
        )}

        <div className={styles.cardBody}>
          <p className={styles.note}>
            Expenses count in monthly and yearly profit only, never in a day. A shared expense is
            split across locations by the team&rsquo;s rota hours that month.
          </p>
        </div>
      </section>
    </>
  );
}
